import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, Video, MapPin, Loader2, ExternalLink, X } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/App';

// Available time slots
const TIME_SLOTS = [
  "09:00", "10:00", "11:00", "12:00", 
  "14:00", "15:00", "16:00", "17:00"
];

// Tour types
const TOUR_TYPES = [
  { id: 'presencial', label: 'Tour Presencial', icon: MapPin, description: 'Visita la propiedad en persona' },
  { id: 'virtual', label: 'Tour Virtual', icon: Video, description: 'Videollamada con un asesor' }
];

const TourRequestButton = ({ property }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1); // 1: type, 2: date/time, 3: contact
  const [tourData, setTourData] = useState({
    tour_type: '',
    date: '',
    time: '',
    name: '',
    phone: '',
    email: '',
    notes: ''
  });

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // Get maximum date (30 days from now)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 30);
    return maxDate.toISOString().split('T')[0];
  };

  // Check if selected date is a weekend
  const isWeekend = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday or Saturday
  };

  // Generate Google Calendar link
  const generateCalendarLink = (tourInfo) => {
    const startDate = new Date(`${tourInfo.date}T${tourInfo.time}:00`);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour
    
    const formatDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const title = encodeURIComponent(`Tour de Propiedad - ${property.name}`);
    const location = encodeURIComponent(`${property.address}, ${property.zone}, ${property.department}`);
    const details = encodeURIComponent(
      `Tour ${tourInfo.tour_type === 'virtual' ? 'Virtual' : 'Presencial'}\n\n` +
      `Propiedad: ${property.name}\n` +
      `Dirección: ${property.address}, ${property.zone}\n` +
      `Precio: $${property.price_usd?.toLocaleString()} USD\n\n` +
      `Contacto: Ikonic Deals\nTel: +502 4254-7204`
    );

    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatDate(startDate)}/${formatDate(endDate)}&details=${details}&location=${location}&sf=true&output=xml`;
  };

  const handleSubmit = async () => {
    if (!tourData.name.trim() || !tourData.phone.trim()) {
      toast.error(t('tour.fillRequired', 'Por favor completa los campos requeridos'));
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/tour-requests`, {
        property_id: property.id,
        property_name: property.name,
        tour_type: tourData.tour_type,
        preferred_date: tourData.date,
        preferred_time: tourData.time,
        name: tourData.name,
        phone: tourData.phone,
        email: tourData.email,
        notes: tourData.notes
      });

      toast.success(t('tour.requestSent', '¡Solicitud enviada! Te contactaremos pronto.'));
      
      // Offer to add to Google Calendar
      const calendarLink = generateCalendarLink(tourData);
      
      // Show success with calendar option
      setStep(4);
      setTourData(prev => ({ ...prev, calendarLink }));
      
    } catch (error) {
      toast.error(t('tour.requestError', 'Error al enviar la solicitud. Intenta de nuevo.'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetModal = () => {
    setStep(1);
    setTourData({
      tour_type: '',
      date: '',
      time: '',
      name: '',
      phone: '',
      email: '',
      notes: ''
    });
  };

  const closeModal = () => {
    setShowModal(false);
    setTimeout(resetModal, 300);
  };

  return (
    <>
      <Button
        onClick={() => setShowModal(true)}
        variant="outline"
        className="w-full border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
        data-testid="schedule-tour-btn"
      >
        <Calendar size={18} className="mr-2" />
        {t('tour.scheduleButton', 'Agendar Tour')}
      </Button>

      <Dialog open={showModal} onOpenChange={closeModal}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <Calendar size={22} className="text-[#D4AF37]" />
              {step === 4 
                ? t('tour.confirmed', '¡Tour Agendado!') 
                : t('tour.scheduleTitle', 'Agendar Tour')
              }
            </DialogTitle>
          </DialogHeader>

          {/* Step 1: Tour Type */}
          {step === 1 && (
            <div className="space-y-4 mt-4">
              <p className="text-[#A3A3A3] text-sm">
                {t('tour.selectType', '¿Cómo te gustaría conocer la propiedad?')}
              </p>
              
              <div className="space-y-3">
                {TOUR_TYPES.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => {
                      setTourData(prev => ({ ...prev, tour_type: type.id }));
                      setStep(2);
                    }}
                    className={`w-full p-4 border rounded-lg text-left transition-all flex items-start gap-3 ${
                      tourData.tour_type === type.id
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                        : 'border-white/10 hover:border-white/30'
                    }`}
                    data-testid={`tour-type-${type.id}`}
                  >
                    <type.icon size={24} className="text-[#D4AF37] mt-0.5" />
                    <div>
                      <p className="text-white font-medium">{type.label}</p>
                      <p className="text-[#A3A3A3] text-sm">{type.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Date & Time */}
          {step === 2 && (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-white">{t('tour.selectDate', 'Fecha preferida')}</Label>
                <Input
                  type="date"
                  min={getMinDate()}
                  max={getMaxDate()}
                  value={tourData.date}
                  onChange={(e) => setTourData(prev => ({ ...prev, date: e.target.value }))}
                  className="bg-[#1a1a1a] border-white/10 text-white"
                  data-testid="tour-date"
                />
                {tourData.date && isWeekend(tourData.date) && (
                  <p className="text-[#D4AF37] text-xs">
                    {t('tour.weekendNote', 'Nota: Los fines de semana tienen horarios limitados')}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-white">{t('tour.selectTime', 'Hora preferida')}</Label>
                <div className="grid grid-cols-4 gap-2">
                  {TIME_SLOTS.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => setTourData(prev => ({ ...prev, time: slot }))}
                      className={`py-2 px-3 text-sm rounded transition-all ${
                        tourData.time === slot
                          ? 'bg-[#D4AF37] text-black font-medium'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                      data-testid={`tour-time-${slot}`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1 border-white/10 text-white hover:bg-white/5"
                >
                  {t('common.back', 'Atrás')}
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!tourData.date || !tourData.time}
                  className="flex-1 bg-[#D4AF37] text-black hover:bg-[#FFE182]"
                >
                  {t('common.continue', 'Continuar')}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Contact Info */}
          {step === 3 && (
            <div className="space-y-4 mt-4">
              <div className="bg-[#1a1a1a] p-3 rounded-lg mb-2">
                <div className="flex items-center gap-2 text-[#D4AF37] text-sm">
                  <Clock size={14} />
                  <span>
                    {new Date(tourData.date).toLocaleDateString('es-GT', { weekday: 'long', day: 'numeric', month: 'long' })} 
                    {' a las '}{tourData.time}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[#A3A3A3] text-sm mt-1">
                  {tourData.tour_type === 'virtual' ? <Video size={14} /> : <MapPin size={14} />}
                  <span>Tour {tourData.tour_type === 'virtual' ? 'Virtual' : 'Presencial'}</span>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="text-white">{t('tour.name', 'Nombre completo')} *</Label>
                  <Input
                    value={tourData.name}
                    onChange={(e) => setTourData(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-[#1a1a1a] border-white/10 text-white mt-1"
                    placeholder="Tu nombre"
                    data-testid="tour-name"
                  />
                </div>

                <div>
                  <Label className="text-white">{t('tour.phone', 'Teléfono')} *</Label>
                  <Input
                    value={tourData.phone}
                    onChange={(e) => setTourData(prev => ({ ...prev, phone: e.target.value }))}
                    className="bg-[#1a1a1a] border-white/10 text-white mt-1"
                    placeholder="+502 1234-5678"
                    data-testid="tour-phone"
                  />
                </div>

                <div>
                  <Label className="text-white">{t('tour.email', 'Email')}</Label>
                  <Input
                    type="email"
                    value={tourData.email}
                    onChange={(e) => setTourData(prev => ({ ...prev, email: e.target.value }))}
                    className="bg-[#1a1a1a] border-white/10 text-white mt-1"
                    placeholder="tu@email.com"
                    data-testid="tour-email"
                  />
                </div>

                <div>
                  <Label className="text-white">{t('tour.notes', 'Notas adicionales')}</Label>
                  <Input
                    value={tourData.notes}
                    onChange={(e) => setTourData(prev => ({ ...prev, notes: e.target.value }))}
                    className="bg-[#1a1a1a] border-white/10 text-white mt-1"
                    placeholder="Preguntas o comentarios..."
                    data-testid="tour-notes"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="flex-1 border-white/10 text-white hover:bg-white/5"
                >
                  {t('common.back', 'Atrás')}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 bg-[#D4AF37] text-black hover:bg-[#FFE182]"
                  data-testid="confirm-tour-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      {t('common.sending', 'Enviando...')}
                    </>
                  ) : (
                    t('tour.confirm', 'Confirmar Tour')
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <div className="space-y-4 mt-4 text-center">
              <div className="w-16 h-16 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto">
                <Calendar size={32} className="text-[#D4AF37]" />
              </div>
              
              <div>
                <p className="text-white">
                  {t('tour.successMessage', 'Tu solicitud de tour ha sido recibida.')}
                </p>
                <p className="text-[#A3A3A3] text-sm mt-1">
                  {t('tour.willContact', 'Te contactaremos para confirmar la cita.')}
                </p>
              </div>

              <div className="bg-[#1a1a1a] p-4 rounded-lg text-left">
                <p className="text-[#A3A3A3] text-xs uppercase mb-2">{t('tour.details', 'Detalles')}</p>
                <p className="text-white text-sm">{property.name}</p>
                <p className="text-[#D4AF37] text-sm">
                  {new Date(tourData.date).toLocaleDateString('es-GT', { weekday: 'long', day: 'numeric', month: 'long' })} - {tourData.time}
                </p>
              </div>

              <a
                href={tourData.calendarLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 bg-white text-black hover:bg-gray-100 rounded-lg font-medium transition-colors"
                data-testid="add-to-calendar-btn"
              >
                <ExternalLink size={18} />
                {t('tour.addToCalendar', 'Añadir a Google Calendar')}
              </a>

              <Button
                onClick={closeModal}
                variant="outline"
                className="w-full border-white/10 text-white hover:bg-white/5"
              >
                {t('common.close', 'Cerrar')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TourRequestButton;
